import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TourButton from '../../components/TourButton/TourButton';
import { getCalendarPageSteps } from '../../tours/calendarPage';
import { useAppContext } from '../../context/AppContext';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Popover from '@mui/material/Popover';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import 'dayjs/locale/it';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en';
import axios from 'axios';
import './Calendar.css';
import { capitalize } from '@mui/material';
import CalendarEventModal from '../../components/Modals/NewCalendarEventModal/CalendarEventModal';

dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(localeData);

/**
 * Custom day component that highlights the entire ISO week (Mon–Sun)
 * of the selected and hovered day.
 */
const WeekPickerDay = ({ day, selectedDay, hoveredDay, adapterLocale, ...pickersDayProps }) => {
    const startSel = selectedDay ? selectedDay.locale(adapterLocale).startOf('week') : null;
    const endSel   = selectedDay ? selectedDay.locale(adapterLocale).startOf('week').add(6, 'day') : null;
    const startHov = hoveredDay  ? hoveredDay.locale(adapterLocale).startOf('week')  : null;
    const endHov   = hoveredDay  ? hoveredDay.locale(adapterLocale).startOf('week').add(6, 'day')  : null;

    const inSelected = startSel && !day.isBefore(startSel, 'day') && !day.isAfter(endSel, 'day');
    const inHovered  = !inSelected && startHov && !day.isBefore(startHov, 'day') && !day.isAfter(endHov, 'day');

    const isFirst = inSelected ? day.isSame(startSel, 'day') : inHovered ? day.isSame(startHov, 'day') : false;
    const isLast  = inSelected ? day.isSame(endSel,   'day') : inHovered ? day.isSame(endHov,   'day') : false;

    return (
        <PickersDay
            {...pickersDayProps}
            day={day}
            disableMargin
            selected={false}
            sx={{
                borderRadius: 0,
                ...(inSelected && {
                    backgroundColor: 'var(--secondary-color)',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'var(--secondary-color)' },
                    '&:focus': { backgroundColor: 'var(--secondary-color)' },
                }),
                ...(inHovered && {
                    backgroundColor: 'var(--tertiary-color)',
                    '&:hover': { backgroundColor: 'var(--tertiary-color)' },
                }),
                ...(isFirst && { borderTopLeftRadius: '50%', borderBottomLeftRadius: '50%' }),
                ...(isLast  && { borderTopRightRadius: '50%', borderBottomRightRadius: '50%' }),
            }}
        />
    );
};

const Calendar = () => {
    const { t, i18n } = useTranslation();
    const { dietist } = useAppContext();

    const MONTH_KEYS = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];

    const today = dayjs();
    const [selectedDay, setSelectedDay] = useState(today);
    const [hoveredDay, setHoveredDay]   = useState(null);
    const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

    // Calendar events for the displayed week
    const [events, setEvents] = useState([]);

    // Cell hover tracking
    const [hoveredCell, setHoveredCell] = useState(null);

    // CalendarEventModal state
    const [calEventModalOpen,  setCalEventModalOpen]  = useState(false);
    const [calEventModalDate,  setCalEventModalDate]  = useState(null);
    const [calEventModalEvent, setCalEventModalEvent] = useState(null);


    // 'it' and 'en-gb' start on Monday; plain 'en' starts on Sunday
    const isItalian    = i18n.language.startsWith('it');
    const adapterLocale = isItalian ? 'it' : 'en';

    // Build the 7 days of the selected week using the active locale's week start
    // (Sunday for EN, Monday for IT/en-gb)
    const weekStart = selectedDay.locale(adapterLocale).startOf('week');
    const weekEnd   = weekStart.add(6, 'day').endOf('day');
    const weekDays  = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));

    /**
     * Build the header label for the current week.
     * - Same month+year  → "March 2026"
     * - Different months, same year → "March-April 2026"
     * - Different months AND years  → "December 2025-January 2026"
     */
    const buildWeekLabel = () => {
        const startMonth = capitalize(t(MONTH_KEYS[weekStart.month()]));
        const endMonth   = capitalize(t(MONTH_KEYS[weekEnd.month()]));
        const startYear  = weekStart.year();
        const endYear    = weekEnd.year();
        if (startYear !== endYear) return `${startMonth} ${startYear}-${endMonth} ${endYear}`;
        if (weekStart.month() !== weekEnd.month()) return `${startMonth}-${endMonth} ${startYear}`;
        return `${startMonth} ${startYear}`;
    };
    const weekLabel = buildWeekLabel();

    // 24 hour slots
    const HOURS = Array.from({ length: 24 }, (_, i) => i);

    /**
     * Fetch calendar events for the currently displayed ISO week.
     * Wrapped in useCallback so the reference is stable within the same week,
     * preventing stale-closure issues in effects and modal callbacks.
     */
    const fetchWeekEvents = useCallback(async () => {
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/events`,
                {
                    params: {
                        startDate: weekStart.toISOString(),
                        endDate:   weekStart.add(6, 'day').endOf('day').toISOString(),
                    },
                    withCredentials: true,
                }
            );
            setEvents(data);
        } catch (error) {
            console.log('ERROR in fetchWeekEvents():', error);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekStart.format('YYYY-MM-DD'), dietist?._id]);

    useEffect(() => {
        fetchWeekEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchWeekEvents]);

    // Re-fetch events whenever the event modal closes (after save, delete, or cancel)
    const prevModalOpen = useRef(false);
    useEffect(() => {
        if (prevModalOpen.current && !calEventModalOpen) {
            fetchWeekEvents();
        }
        prevModalOpen.current = calEventModalOpen;
    }, [calEventModalOpen, fetchWeekEvents]);

    /**
     * Navigate the selected week forward or backward by one week.
     * @param {number} direction - +1 for next week, -1 for previous week
     */
    const handleWeekNav = (direction) => {
        setSelectedDay(prev => prev.add(direction * 7, 'day'));
    };

    /** Jump the selected week back to the current week. */
    const handleTodayClick = () => {
        setSelectedDay(today);
    };

    /**
     * Called when a day is clicked in the popover calendar.
     * Selects that day's week and closes the popover.
     * @param {dayjs.Dayjs} day
     */
    const handleWeekSelect = (day) => {
        setSelectedDay(day);
        setCalendarAnchorEl(null);
    };

    /**
     * Open the add-event modal pre-filled with the clicked cell's date.
     * @param {dayjs.Dayjs} day
     * @param {number}  hour   - 0-23
     * @param {boolean} isHalf - true if the half-hour slot was clicked
     */
    const handleCellClick = (day, hour, isHalf) => {
        const dateStr = day.hour(hour).minute(isHalf ? 30 : 0).second(0).format('YYYY-MM-DDTHH:mm');
        setCalEventModalDate(dateStr);
        setCalEventModalEvent(null);
        setCalEventModalOpen(true);
    };

    /**
     * Open the edit-event modal for the clicked event.
     * @param {object} event - the calendar event object
     * @param {React.MouseEvent} e
     */
    const handleEventClick = (event, e) => {
        e.stopPropagation();
        setCalEventModalEvent(event);
        setCalEventModalDate(null);
        setCalEventModalOpen(true);
    };

    /**
     * Return events that start in a specific half-hour cell.
     * @param {number}  dayIdx
     * @param {number}  hour
     * @param {boolean} isHalf
     */
    const getEventsForCell = (dayIdx, hour, isHalf) => {
        return events.filter(ev => {
            const start = dayjs(ev.startDate);
            return (
                start.isSame(weekDays[dayIdx], 'day') &&
                start.hour() === hour &&
                (isHalf ? start.minute() >= 30 : start.minute() < 30)
            );
        });
    };

    /**
     * Calculate the pixel height of an event block based on its duration.
     * Each 30-minute slot is 30px tall.
     * @param {object} event
     */
    const getEventHeight = (event) => {
        const durationMinutes = dayjs(event.endDate).diff(dayjs(event.startDate), 'minute');
        return Math.max(30, Math.round((durationMinutes / 30) * 30));
    };

    return (
        <>
            <div id='calendar'>
                <div id='calendar-filters-row' data-tour="cal-filters">
                    <div id='calendar-filter-today' onClick={handleTodayClick}>
                        <CalendarTodayIcon sx={{ color: '#555', marginRight: '5px' }} />
                        <span>{capitalize(t("today"))}</span>
                    </div>
                    <div id='calendar-filter-arrows'>
                        <ArrowBackIosIcon sx={{ color: '#555' }} onClick={() => handleWeekNav(-1)} />
                        <ArrowForwardIosIcon sx={{ color: '#555' }} onClick={() => handleWeekNav(1)} />
                    </div>
                    <div id='calendar-filter-month' onClick={(e) => setCalendarAnchorEl(e.currentTarget)}>
                        <span>{weekLabel}</span>
                        <ArrowDropDownIcon sx={{ color: '#555' }} />
                    </div>
                    <div id='calendar-tour-wrapper'>
                        <TourButton steps={getCalendarPageSteps(t)} />
                    </div>
                </div>
                <div id='calendar-grid-wrapper'>
                    <div id='calendar-grid' data-tour="cal-grid">

                        {/* ── Header row ── */}
                        <div className='cal-header-cell cal-time-header' />
                        {weekDays.map((day, i) => {
                            const isToday = day.isSame(today, 'day');
                            return (
                                <div key={i} className='cal-header-cell cal-day-header'>
                                    <span className={`cal-day-number${isToday ? ' today-circle' : ''}`}>
                                        {day.date()} {capitalize(day.locale(adapterLocale).format('MMM'))}
                                    </span>
                                    <span className='cal-day-name'>
                                        {capitalize(day.locale(adapterLocale).format('ddd'))}
                                    </span>
                                </div>
                            );
                        })}

                        {/* ── Hour rows ── */}
                        {HOURS.map(hour => (
                            <React.Fragment key={hour}>
                                {/* Full-hour row */}
                                <div className='cal-time-cell cal-hour-label'>
                                    {String(hour).padStart(2, '0')}:00
                                </div>
                                {weekDays.map((day, di) => (
                                    <div
                                        key={di}
                                        className='cal-event-cell cal-full-hour-cell'
                                        style={{
                                            backgroundColor:
                                                hoveredCell?.dayIdx === di && hoveredCell?.hour === hour && !hoveredCell?.isHalf
                                                    ? 'var(--tertiary-color)'
                                                    : undefined
                                        }}
                                        onMouseEnter={() => setHoveredCell({ dayIdx: di, hour, isHalf: false })}
                                        onMouseLeave={() => setHoveredCell(null)}
                                        onClick={() => handleCellClick(day, hour, false)}
                                    >
                                        {getEventsForCell(di, hour, false).map(ev => (
                                            <div
                                                key={ev._id}
                                                className='cal-event-block'
                                                style={{ height: `${getEventHeight(ev)}px` }}
                                                onClick={(e) => handleEventClick(ev, e)}
                                            >
                                                <span className='cal-event-block-title'>
                                                    {ev.type ? t(`eventTypes.${ev.type}`) : ''}
                                                    {ev.client ? ` - ${ev.client.name} ${ev.client.surname}` : ''}
                                                </span>
                                                {ev.description && (
                                                    <span className='cal-event-block-desc'>{ev.description}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                {/* Half-hour row */}
                                <div className='cal-time-cell cal-half-label' />
                                {weekDays.map((day, di) => (
                                    <div
                                        key={`${di}h`}
                                        className='cal-event-cell cal-half-hour-cell'
                                        style={{
                                            backgroundColor:
                                                hoveredCell?.dayIdx === di && hoveredCell?.hour === hour && hoveredCell?.isHalf
                                                    ? 'var(--tertiary-color)'
                                                    : undefined
                                        }}
                                        onMouseEnter={() => setHoveredCell({ dayIdx: di, hour, isHalf: true })}
                                        onMouseLeave={() => setHoveredCell(null)}
                                        onClick={() => handleCellClick(day, hour, true)}
                                    >
                                        {getEventsForCell(di, hour, true).map(ev => (
                                            <div
                                                key={ev._id}
                                                className='cal-event-block'
                                                style={{ height: `${getEventHeight(ev)}px` }}
                                                onClick={(e) => handleEventClick(ev, e)}
                                            >
                                                <span className='cal-event-block-title'>
                                                    {ev.type ? t(`eventTypes.${ev.type}`) : ''}
                                                    {ev.client ? ` - ${ev.client.name} ${ev.client.surname}` : ''}
                                                </span>
                                                {ev.description && (
                                                    <span className='cal-event-block-desc'>{ev.description}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </React.Fragment>
                        ))}

                    </div>
                </div>

                {calEventModalOpen && (
                    <CalendarEventModal
                        setShowModal={setCalEventModalOpen}
                        date={calEventModalDate}
                        event={calEventModalEvent}
                        onSubmit={fetchWeekEvents}
                    />
                )}

                <Popover
                    open={Boolean(calendarAnchorEl)}
                    anchorEl={calendarAnchorEl}
                    onClose={() => setCalendarAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                        <DateCalendar
                            value={selectedDay}
                            onChange={handleWeekSelect}
                            slots={{ day: WeekPickerDay }}
                            slotProps={{
                                day: (ownerState) => ({
                                    selectedDay,
                                    hoveredDay,
                                    adapterLocale,
                                    onPointerEnter: () => setHoveredDay(ownerState.day),
                                    onPointerLeave: () => setHoveredDay(null),
                                }),
                            }}
                        />
                    </LocalizationProvider>
                </Popover>
            </div>
        </>
    );
};

export default Calendar;